import React, { Component } from "react";
import {
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
  Tooltip,
  IconButton,
  Button,
  withStyles,
  Theme,
  createStyles,
  WithStyles,
} from "@material-ui/core";
import { connect } from "react-redux";
import { GlobalState } from "./../../types/globalState";
import { Tournament, TournamentGames, ScoreSystem } from "./../../types/tournament";
import LeaderboardRow, { LeaderboardPlayer } from "./leaderboardRow";
import Trophy from "../../assets/images/trophy.png";
import { CloudDownload } from "@material-ui/icons";
import { exportResultToPDF, exportResultToImageFile } from "../../utils/GameReportGenerator/pdf";
import { showAuxiliaryDialog } from "../SharedComponents/AuxiliaryDialog";
import { ShareFileButton } from "../../components/SharedComponents/ShareButton";
import TournamentHoleByHoleDialog from "./TournamentHoleByHoleDialog";
import { Player } from "./../../types/player";
import { getHandicapDisplay } from "utils";
import { User, Rules } from "../../types/user";
import { Game } from "../../types/game";

const refId = (ref: unknown): string => {
  if (ref == null) return "";
  if (typeof ref === "object" && "_id" in (ref as object)) return String((ref as { _id: unknown })._id);
  return String(ref);
};

const canSeeAnyPlayerRoundBreakdown = (user: User | undefined, tournament: Tournament, games?: Game[]): boolean => {
  if (!user?._id) return false;
  const uid = String(user._id);
  if (user.rule === Rules.Admin) return true;
  if (refId(tournament.owner) === uid) return true;
  return (games || []).some(game => {
    if (refId(game.owner) === uid) return true;
    return (game.organizers || []).some(org => refId(org) === uid);
  });
};

export interface Props extends WithStyles {
  tournamentGames: TournamentGames;
  tournament: Tournament;
  embedded?: boolean;
  user?: User;
}

class TournamentLeaderboard extends Component<Props> {
  handleRowClick = (player: LeaderboardPlayer) => {
    const { tournament, tournamentGames } = this.props;
    const playerPoints = tournamentGames.points.find(p => p._id === player._id)?.points || 0;
    const displayName = [player.name, player.lastName].filter(Boolean).join(" ").trim();
    const subtitle = displayName
      ? `${displayName} · PH ${getHandicapDisplay(player.hcp)}`
      : `PH ${getHandicapDisplay(player.hcp)}`;

    showAuxiliaryDialog(
      <TournamentHoleByHoleDialog
        player={player as unknown as Player}
        points={playerPoints}
        tournament={tournament}
      />,
      "md",
      true,
      true,
      true,
      "Round breakdown",
      false,
      subtitle,
      false,
    );
  };

  render() {
    const { tournament, tournamentGames, classes, user } = this.props;
    const points = tournamentGames?.points || [];
    const canSeeAllBreakdowns = canSeeAnyPlayerRoundBreakdown(user, tournament, tournamentGames?.games);
    const tournamentForExport: any = {
      name: tournament.name,
      players: points,
      course: { holesNumber: 18 },
    };

    const canExport =
      !(
        [ScoreSystem.TOTAL_GROSS, ScoreSystem.TOTAL_NETT, ScoreSystem.TOTAL_POINTS].includes(
          tournament.selectedScoreSystem,
        ) || !points.length
      );

    const { embedded } = this.props;

    return (
      <div className={`${classes.root} ${embedded ? classes.rootEmbedded : ""}`}>
        <div className={classes.trophyWatermark} aria-hidden />
        {canExport ? (
          <div className={classes.exportRow}>
            <Tooltip title="Export result" leaveTouchDelay={2000}>
              <IconButton
                size="small"
                className={classes.exportButton}
                aria-label="Export tournament result"
                onClick={() => {
                  showAuxiliaryDialog(
                    <div className="flex-column">
                      <ShareFileButton
                        title={`SCHALA - ${tournament.name} Tournament Result`}
                        text={`Here is result of ${tournament.name} tournament which has been played using SCHALA`}
                        url={`https://schala.golf`}
                        getFiles={async () =>
                          await exportResultToImageFile(`TournamentResult`, tournamentForExport, "", true)
                        }
                      />
                      <Button onClick={() => exportResultToPDF(tournamentForExport, "", true)}>Download</Button>
                    </div>,
                    "xl",
                    false,
                    true,
                    true,
                    "Choose action",
                  );
                }}
              >
                <CloudDownload />
              </IconButton>
            </Tooltip>
          </div>
        ) : null}
        <div className={classes.tableWrap}>
          <TableContainer component={Paper} elevation={0} className={classes.tableContainer}>
            <Table aria-label="Tournament leaderboard" size="small" className={classes.table}>
              <TableHead>
                <TableRow>
                  <TableCell className={`${classes.th} ${classes.colPos}`}>Pos.</TableCell>
                  <TableCell className={`${classes.th} ${classes.colIcon}`} aria-hidden />
                  <TableCell className={`${classes.th} ${classes.colName}`}>Name</TableCell>
                  <TableCell className={`${classes.th} ${classes.colScore}`} align="right">
                    {tournament.selectedScoreSystem === ScoreSystem.TOTAL_BLOBS ? "Total" : "Score"}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {points.length ? (
                  points.map((player, index) => (
                    <LeaderboardRow
                      player={player}
                      key={player._id || index}
                      index={index}
                      highlighted={Boolean(user?._id) && String(player._id) === String(user?._id)}
                      clickable={canSeeAllBreakdowns || String(player._id) === String(user?._id)}
                      onClick={this.handleRowClick}
                      rowClasses={{
                        cellPos: classes.cellPos,
                        cellIcon: classes.cellIcon,
                        cellName: classes.cellName,
                        cellScore: classes.cellScore,
                        rowClickable: classes.rowClickable,
                      }}
                    />
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      No leaderboard data available for this tournament.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </div>
      </div>
    );
  }
}

const styles = (theme: Theme) =>
  createStyles({
    root: {
      position: "relative",
      width: "100%",
      maxWidth: "100%",
      boxSizing: "border-box",
      padding: theme.spacing(1.25, 1.5, 1.5),
      backgroundColor: "#fafbfc",
      flex: 1,
      minHeight: 0,
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",

      [theme.breakpoints.up("sm")]: {
        padding: theme.spacing(1.5, 2, 2),
      },
    },
    rootEmbedded: {
      paddingTop: theme.spacing(0.75),
      paddingLeft: theme.spacing(1),
      paddingRight: theme.spacing(1),
      paddingBottom: theme.spacing(1.25),
      backgroundColor: "transparent",

      [theme.breakpoints.up("sm")]: {
        paddingLeft: theme.spacing(1.25),
        paddingRight: theme.spacing(1.25),
      },
    },
    exportRow: {
      position: "relative",
      zIndex: 1,
      display: "flex",
      justifyContent: "flex-end",
      marginBottom: theme.spacing(0.75),
      minHeight: 0,
    },
    exportButton: {
      color: "#64748b",
      padding: theme.spacing(0.75),
      border: "1px solid rgba(226, 232, 240, 0.95)",
      backgroundColor: "#ffffff",
      transition: theme.transitions.create(["transform", "background-color", "color"], {
        duration: theme.transitions.duration.shortest,
      }),
      "&:hover": {
        backgroundColor: "#f8fafc",
        color: "#334155",
      },
      "&:active": {
        transform: "scale(0.96)",
      },
    },
    tableWrap: {
      position: "relative",
      zIndex: 1,
      flex: 1,
      minHeight: 0,
      width: "100%",
      maxWidth: "100%",
      borderRadius: 10,
      border: `1px solid rgba(226, 232, 240, 0.95)`,
      boxShadow: "0 14px 32px -18px rgba(15, 23, 42, 0.12)",
      overflow: "auto",
      WebkitOverflowScrolling: "touch",
      backgroundColor: "rgba(255, 255, 255, 0.1)",
    },
    trophyWatermark: {
      position: "absolute",
      inset: 0,
      backgroundImage: `url(${Trophy})`,
      backgroundRepeat: "no-repeat",
      backgroundPosition: "center",
      backgroundSize: "168px 187px",
      opacity: 0.38,
      pointerEvents: "none",
      zIndex: 0,
    },
    tableContainer: {
      position: "relative",
      zIndex: 1,
      width: "100%",
      maxWidth: "100%",
      overflowX: "hidden",
      backgroundColor: "transparent",
      boxShadow: "none",
      paddingBottom: "calc(65px + env(safe-area-inset-bottom, 0px))",

      "&.MuiPaper-root": {
        backgroundColor: "transparent",
      },
    },
    table: {
      width: "100%",
      maxWidth: "100%",
      tableLayout: "fixed",
      backgroundColor: "transparent",
      "& thead .MuiTableCell-root": {
        backgroundColor: "rgba(248, 250, 252, 0.2)",
      },
      "& tbody .MuiTableCell-root": {
        backgroundColor: "rgba(255, 255, 255, 0.68)",
      },
      "& tbody tr": {
        verticalAlign: "middle",
      },
    },
    th: {
      fontWeight: 700,
      fontSize: "0.68rem",
      padding: theme.spacing(0.5, 0.75),
      lineHeight: 1.2,
      whiteSpace: "nowrap",
      [theme.breakpoints.up("sm")]: {
        fontSize: theme.typography.caption.fontSize,
        padding: theme.spacing(1, 1.5),
      },
    },
    colPos: {
      width: 36,
      [theme.breakpoints.up("sm")]: {
        width: 52,
      },
    },
    colIcon: {
      width: 40,
      paddingLeft: theme.spacing(0.5),
      paddingRight: theme.spacing(0.5),
      [theme.breakpoints.down("sm")]: {
        display: "none",
      },
    },
    colName: {
      width: "auto",
      minWidth: 0,
    },
    colScore: {
      width: 56,
      paddingRight: `${theme.spacing(1.25)}px !important`,
      [theme.breakpoints.up("sm")]: {
        width: 76,
        paddingRight: `${theme.spacing(1.5)}px !important`,
      },
    },
    cellPos: {
      width: 36,
      padding: theme.spacing(0.5, 0.75),
      fontSize: "0.8125rem",
      [theme.breakpoints.up("sm")]: {
        width: 52,
        padding: theme.spacing(1, 1.5),
        fontSize: theme.typography.body2.fontSize,
      },
    },
    cellIcon: {
      width: 40,
      padding: theme.spacing(0.25, 0.5),
      [theme.breakpoints.down("sm")]: {
        display: "none",
      },
      "& svg": {
        fontSize: "1.1rem",
        [theme.breakpoints.up("sm")]: {
          fontSize: "1.25rem",
        },
      },
    },
    cellName: {
      minWidth: 0,
      padding: theme.spacing(0.5, 0.75),
      fontSize: "0.8125rem",
      overflow: "hidden",
      textOverflow: "ellipsis",
      [theme.breakpoints.up("sm")]: {
        padding: theme.spacing(1, 1.5),
        fontSize: theme.typography.body2.fontSize,
      },
      "& span": {
        whiteSpace: "nowrap",
      },
    },
    cellScore: {
      width: 56,
      padding: theme.spacing(0.5, 0.75),
      paddingRight: `${theme.spacing(1.25)}px !important`,
      fontSize: "0.8125rem",
      fontWeight: 600,
      fontVariantNumeric: "tabular-nums",
      whiteSpace: "nowrap",
      color: "#0f172a",
      [theme.breakpoints.up("sm")]: {
        width: 76,
        padding: theme.spacing(1, 1.5),
        paddingRight: `${theme.spacing(1.5)}px !important`,
        fontSize: theme.typography.body2.fontSize,
      },
    },
    rowClickable: {
      cursor: "pointer",
    },
  });

const mapStateToProps = (state: GlobalState) => {
  const { tournamentGames } = state.tournaments;
  return {
    tournamentGames,
    user: state.auth.user,
  };
};

export default connect(mapStateToProps)(withStyles(styles)(TournamentLeaderboard));
